import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { InferType } from 'yup';

import {
  RegistrationPersonItemDataFragment,
  RegistrationPersonUpdateInput,
} from '../../../data/types/api/graphql';
import { Answer, personSchema } from './person-schema';

export type RegistrationPersonFormValues = InferType<typeof personSchema>;

const toAnswer = (value: boolean | null | undefined): Answer => {
  if (value === null || value === undefined) return '';

  return value ? 'yes' : 'no';
};

const fromAnswer = (value: string | null | undefined): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;

  return null;
};

/** Empty optional text goes back as null, as the server stores it. */
const orNull = (value: string | null | undefined) =>
  isEmpty(value) ? null : value;

const toFormValues = (
  person: RegistrationPersonItemDataFragment
): RegistrationPersonFormValues => ({
  initials: person.initials ?? '',
  firstName: person.firstName,
  lastName: person.lastName,
  birthName: person.birthName ?? '',
  birthDate: person.birthDate,
  gender: person.gender,
  mobileApp: person.mobileApp,
  email: person.email,
  phoneNumber: person.phoneNumber,
  bsn: person.bsn ?? '',
  street: person.street ?? '',
  houseNumber: person.houseNumber ?? '',
  houseNumberAddition: person.houseNumberAddition ?? '',
  postalCode: person.postalCode ?? '',
  city: person.city ?? '',
  minorConsent: toAnswer(person.minorConsent),
  pharmacyName: person.pharmacyName ?? '',
  previousGpInNetherlands: toAnswer(person.previousGpInNetherlands),
  previousGpName: person.previousGpName ?? '',
  previousGpCity: person.previousGpCity ?? '',
  previousGpNote: person.previousGpNote ?? '',
  recordTransferConsent: toAnswer(person.recordTransferConsent),
  recordTransferDeclineReason: person.recordTransferDeclineReason ?? '',
  // An older registration may have no insurance type; the form then asks for one.
  insuranceType: person.insuranceType ?? (undefined as never),
  insuranceNote: person.insuranceNote ?? '',
  insuranceCompany: person.insuranceCompany ?? '',
  insurancePolicyNumber: person.insurancePolicyNumber ?? '',
  lspConsent: toAnswer(person.lspConsent),
  contactPersonName: person.contactPersonName ?? '',
  contactPersonRelation: person.contactPersonRelation ?? '',
  contactPersonPhoneNumber: person.contactPersonPhoneNumber ?? '',
  remarks: person.remarks ?? '',
  language: person.language,
  mfaAuthenticationType: person.mfaAuthenticationType,
  dutchInsurance: person.dutchInsurance,
  noShowProtocol: person.noShowProtocol,
});

export const toUpdateInput = (
  registrationPersonId: string,
  values: RegistrationPersonFormValues
): RegistrationPersonUpdateInput => ({
  registrationPersonId,
  initials: values.initials,
  firstName: values.firstName,
  lastName: values.lastName,
  birthName: orNull(values.birthName),
  birthDate: values.birthDate,
  gender: values.gender,
  mobileApp: values.mobileApp === true,
  email: values.email ?? '',
  phoneNumber: values.phoneNumber.replace(/\s/g, ''),
  // Empty clears the BSN on the server.
  bsn: orNull(values.bsn),
  street: orNull(values.street),
  houseNumber: orNull(values.houseNumber),
  houseNumberAddition: orNull(values.houseNumberAddition),
  postalCode: orNull(values.postalCode),
  city: orNull(values.city),
  minorConsent: fromAnswer(values.minorConsent),
  pharmacyName: orNull(values.pharmacyName),
  previousGpInNetherlands: fromAnswer(values.previousGpInNetherlands),
  previousGpName: orNull(values.previousGpName),
  previousGpCity: orNull(values.previousGpCity),
  previousGpNote: orNull(values.previousGpNote),
  recordTransferConsent: fromAnswer(values.recordTransferConsent),
  recordTransferDeclineReason: orNull(values.recordTransferDeclineReason),
  insuranceType: values.insuranceType,
  insuranceNote: orNull(values.insuranceNote),
  insuranceCompany: orNull(values.insuranceCompany),
  insurancePolicyNumber: orNull(values.insurancePolicyNumber),
  lspConsent: fromAnswer(values.lspConsent),
  contactPersonName: orNull(values.contactPersonName),
  contactPersonRelation: orNull(values.contactPersonRelation),
  contactPersonPhoneNumber: orNull(values.contactPersonPhoneNumber),
  remarks: orNull(values.remarks),
  language: values.language,
  mfaAuthenticationType: values.mfaAuthenticationType,
  dutchInsurance: values.dutchInsurance === true,
  noShowProtocol: values.noShowProtocol === true,
});

export function useRegistrationPersonForm(
  person: RegistrationPersonItemDataFragment
) {
  const values = useMemo(() => toFormValues(person), [person]);

  const resolver = useMemo(() => yupResolver(personSchema), []);

  const form = useForm<RegistrationPersonFormValues>({
    resolver,
    defaultValues: values,
    mode: 'onBlur',
  });

  return {
    form,
    formIsValid: useMemo(() => form.formState.isValid, [form.formState]),
  };
}
